// Every check in the codebase asks "is this ADMIN?", so a new role starts with
// a member's reach: `guest` is narrowed explicitly, never implicitly.
export const USER_ROLES = {
  ADMIN: "admin",
  MEMBER: "member",
  GUEST: "guest",
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];
export const ALL_USER_ROLES = Object.values(USER_ROLES);

/** Who may sign up unannounced. Anyone else needs a nominative invitation. */
export const ALLOWED_EMAIL_DOMAIN = "@impulselab.ai";

/** How long a nominative invitation stays usable. */
export const INVITATION_TTL_DAYS = 14;

/**
 * Public API scopes, in `resource:action` form. A Better Auth API key stores
 * them as its `permissions` map; a human session (browser cookie or a CLI
 * device-flow bearer token) implicitly holds every scope and is governed by
 * the user's role instead.
 *
 * Deliberately absent: `approvals:decide`, anything under `keys`, and
 * `devices:register` / `devices:revoke`. Guardrail decisions (VISION "never
 * without explicit human validation") and credential issuance are human-only
 * actions, no key can ever be granted them, so a leaked key can neither
 * approve a merge nor mint another key. Enrolling one of your machines and
 * cutting it off are the same kind of act: the machine IS a credential, so a
 * key must not be able to enrol another one or disown the one it runs on.
 */
export const API_SCOPES = [
  "sessions:read",
  "sessions:export",
  "sessions:write",
  "questions:read",
  "questions:answer",
  "approvals:read",
  "catalog:read",
  "pull_requests:read",
  "devices:read",
  "devices:write",
  "browser:read",
  "browser:write",
  "accounts:read",
  "inference:write",
] as const;

export type ApiScope = (typeof API_SCOPES)[number];

/** Read-only, minus the browser vault: it names the services a human uses. */
export const DEFAULT_API_SCOPES: readonly ApiScope[] = API_SCOPES.filter(
  (scope) => scope.endsWith(":read") && !scope.startsWith("browser:"),
);

/**
 * What the daemon on a paired machine is allowed to be. It is the equivalent
 * of a Daytona sandbox, not of its owner: open its channel, run what it is
 * sent, write the transcript back, publish its folders and its metrics.
 *
 * Everything else is absent on purpose, and each absence is a stolen-laptop
 * answer. No `browser:*`: the daemon must not read the vault of cookies its
 * owner captured. No `accounts:read`: it must not enumerate the subscription
 * pool. No `inference:write`: it must not spend on the org's gateway outside a
 * turn. No `approvals:read`, no `catalog:read`, no `pull_requests:read`. And
 * because `devices:register` / `devices:revoke` are not scopes at all, a
 * machine can neither enrol another one nor disown itself, those routes take
 * a human session, which the daemon no longer holds.
 */
export const MACHINE_API_SCOPES: readonly ApiScope[] = [
  "devices:read",
  "devices:write",
  "sessions:read",
  "sessions:write",
  "questions:read",
];

/** Prefix stamped on every issued key, so leaks are greppable. */
export const API_KEY_PREFIX = "hepha_";

export const MACHINE_KEY_NAME_PREFIX = "machine:";

/**
 * Better Auth's own ceiling is 32, which `machine:<deviceId>` overruns by two:
 * a ULID is 26 characters and the label is eight. Raised rather than the name
 * shortened, the name is what a human reads next to a key in the UI, and the
 * device it names is the whole of its meaning.
 */
export const API_KEY_MAX_NAME_LENGTH = 64;

/**
 * Browser session lifetime. Signing in is always "remember me": there is no
 * opt-out in the UI, so every sign-in path writes a PERSISTENT cookie that
 * survives closing the browser. `UPDATE_AGE` slides the expiry forward on use,
 * so an active human never sees the sign-in screen again.
 */
export const SESSION = {
  /** Cookie + session row lifetime: 30 days. */
  EXPIRES_IN: 60 * 60 * 24 * 30,
  /** Refresh the expiry at most once a day, to avoid a write per request. */
  UPDATE_AGE: 60 * 60 * 24,
  /** Cache the session row in memory for this long, to avoid a read per request. */
  COOKIE_CACHE_MAX_AGE: 60,
} as const;

/**
 * OAuth 2.0 device authorization grant (RFC 8628), how the CLI logs a human
 * in without ever handling their password: it asks for a user code, the human
 * approves it on {BASE_URL}{VERIFICATION_PATH} in an already-signed-in
 * browser, and the CLI exchanges the device code for a session token.
 */
export const DEVICE_AUTHORIZATION = {
  /** Only these clients may open a device flow. */
  CLIENT_IDS: ["hepha-cli", "hepha-extension"],
  /** Lifetime of an unapproved user code. */
  EXPIRES_IN: "15m",
  /** Minimum delay the CLI must wait between two token polls. */
  INTERVAL: "5s",
  /** Page a human visits to approve a pending device. */
  VERIFICATION_PATH: "/device",
  /**
   * Per-IP ceiling on `/device/token`. The window is deliberately SHORTER than
   * INTERVAL: Better Auth only resets its counter when two requests are further
   * apart than the window, so a 5s poll under the default 60s window never reset
   * it and every login 429'd partway through the code's lifetime.
   */
  POLL_RATE_LIMIT: { WINDOW_SECONDS: 4, MAX_REQUESTS: 10 },
} as const;

// Copied by hand into the extension, which ships no build step and cannot
// import this; `apps/browser-extension/test/limits-match-server.test.mjs` fails on drift.
export const BROWSER_CAPTURE_LIMITS = {
  COOKIES: 300,
  ORIGINS: 25,
  STORAGE_KEYS_PER_ORIGIN: 300,
  NAME_LENGTH: 1024,
  VALUE_LENGTH: 32_768,
  /** `JSON.stringify(state).length`: UTF-16 code units, not bytes. */
  TOTAL_CHARS: 1_000_000,
  LABEL_LENGTH: 120,
  USER_AGENT_LENGTH: 512,
  LOCALE_LENGTH: 64,
  EXTENSION_VERSION_LENGTH: 32,
  DOMAINS_PER_USER: 200,
} as const;

/**
 * Bounds of a registry skill, shared by every write path so the registry can
 * never hold a skill the edit form refuses to save back.
 *
 * The caps used to be restated per route, so a skill written through one path
 * could violate another's rule and become uneditable for good.
 */
export const SKILL_LIMITS = {
  /** Kebab-case slug, matched verbatim by `buildHarnessSkills`. */
  NAME_PATTERN: /^[a-z0-9]+(-[a-z0-9]+)*$/,
  NAME_MAX_LENGTH: 80,
  /** Not a title: the full "use this when…" trigger list drives matching. */
  DESCRIPTION_MAX_LENGTH: 2000,
  /** Relative POSIX path of a supporting file bundled with the skill. */
  FILE_PATH_MAX_LENGTH: 200,
  /** One activation scope entry: a repo full name or a channel id. */
  ACTIVATION_SCOPE_MAX_LENGTH: 120,
} as const;

/** Reserved: the harness materializes the skill body as SKILL.md. */
export const SKILL_BODY_FILENAME = "skill.md";

/** The auto-maintained global review-lessons skill; per-repo names come from
 *  utils/review-lesson-skill-name.ts. */
export const REVIEW_LESSONS_SKILL_NAME = "review-lessons";

/** The organization-wide lessons of Slides chats, live as soon as recorded. */
export const SLIDES_LESSONS_SKILL_NAME = "slides-lessons";

// Derived by Chrome from the RSA key in `apps/browser-extension/manifest.json`.
// One exact id, never a wildcard: trusted origins also allowlist redirects.
export const BROWSER_EXTENSION_ORIGIN =
  "chrome-extension://ieppdghbneelaninllkeimfcdnccbblj";

// Human code-quality rating: every criterion of the rubric is scored on this
// scale. Client-safe, so the rating card can render the buttons without pulling
// in the server-only database entry.
export const QUALITY_SCORE_MIN = 1;

export const QUALITY_SCORE_MAX = 5;

/** rebase_only | queued, mirrors `pull_request_merge_intent_mode`. */
export const MERGE_INTENT_MODES = ["rebase_only", "queued"] as const;

/** How long an unattached auto-rebase claim keeps exclusive branch ownership. */
export const AUTO_REBASE_CLAIM_MS = 30 * 60 * 1000;
